import React, { useEffect } from 'react';
import { SidebarMenu } from './SidebarMenu';
import { SidebarBack } from './SidebarBack';
import styles from './Sidebar.module.scss';
import { cls } from '../View/utils';
import { useSidebar } from './hooks';
import { blur, isLRUDKey, triggerFocus } from '../navigation/utils';
import { useEventListener } from '../hooks/useEventListener';
import { SidebarColumn } from './types';
import { Keys } from '../utils/keys';
import { isClient } from '../../common/utils';
import { useAriaLive } from '../contexts/AriaLiveContext/AriaLiveContext';
import { getTranslation } from '../localizations/translations';
import { useAppFeaturesContext } from '../contexts/AppFeaturesContext/AppFeaturesContext';
import { useSidebarContext } from '../contexts/SidebarContext/SidebarContext';
import { App } from '@yleisradio/areena-types/domain/app';

export const classNames = {
  sidebarOpen: 'sidebarOpen',
  sidebarHidden: 'sidebarHidden',
  sidebarMenuContainer: 'sidebarMenuContainer',
};

type Props = {
  appData: App | undefined;
};

const Sidebar: React.FC<Props> = ({ appData }) => {
  const {
    closeSidebar: close,
    openSidebar: open,
    isSidebarMenu: isMenu,
    isSidebarOpen: isOpen,
    isSidebarVisible: isVisible,
  } = useSidebarContext();

  const {
    closeApp,
    focus,
    goBackOrGoHome,
    groupings,
    isKeyboardMethod,
    navigate,
  } = useSidebar({
    close,
    isMenu,
    isOpen,
    isVisible,
    appData,
  });

  const { languageCode } = useAppFeaturesContext();
  const { announcePolite } = useAriaLive();

  useEffect(() => {
    if (isOpen) {
      announcePolite(getTranslation('a11y_sidebar_open', languageCode));
    } else {
      announcePolite(getTranslation('a11y_sidebar_close', languageCode));
    }
  }, [isOpen, announcePolite, languageCode]);

  useEffect(() => {
    if (focus) {
      if (isOpen && isKeyboardMethod) {
        triggerFocus(focus, true);
      } else {
        blur(focus);
      }
    }
  }, [focus, isOpen, isKeyboardMethod]);

  const eventTarget = isClient() ? document : undefined;
  useEventListener(
    'keydown',
    e => {
      if (isKeyboardMethod && isVisible && isOpen) {
        let isKeyCaptured = true;

        if (Keys.VK_ENTER === e.keyCode) {
          const activeElement = document.activeElement;

          if (
            focus &&
            activeElement &&
            activeElement.id === focus.id &&
            'click' in activeElement
          ) {
            (activeElement as HTMLElement).click();
          }
        } else if ([Keys.VK_BACK, Keys.VK_BACKSPACE].includes(e.keyCode)) {
          if (isMenu) {
            closeApp();
          } else {
            goBackOrGoHome();
          }
        } else if (isLRUDKey(e.keyCode)) {
          navigate(e);
        } else {
          isKeyCaptured = false;
        }

        if (isKeyCaptured) {
          e.preventDefault();
        }
      }
    },
    eventTarget
  );

  const className = cls({
    [styles.sidebar]: true,
    [classNames.sidebarHidden]: !isVisible,
    [classNames.sidebarMenuContainer]: isMenu,
    [classNames.sidebarOpen]: isMenu && isOpen,
  });

  const overlayClassName = cls({
    [styles.sidebarOverlay]: true,
    [classNames.sidebarOpen]: isOpen && isMenu,
  });

  function toggle() {
    if (isOpen) {
      close();
    } else {
      open();
    }
  }

  return (
    <>
      <div className={overlayClassName} onClick={close} />
      <div
        className={className}
        data-testid={`sidebar-menu-container-${isOpen ? 'open' : 'closed'}`}
      >
        {isMenu ? (
          <SidebarMenu groupings={groupings} toggleOpenSidebar={toggle} />
        ) : (
          <SidebarBack closeSidebar={close} id={`0/${SidebarColumn.Back}/0`} />
        )}
      </div>
    </>
  );
};

export default Sidebar;
